/**
 * MNH-style menu access: each link has permissions; hidden at render if user lacks any.
 * Maps Credora routes to permissions (mirrors credora360-api/app/core/rbac.py).
 */
import { MENUITEMS } from "../Layout/Sidebar/Menu";
import { DEFAULT_LAYOUT_SLUG } from "./layout";
import { stripAppBasename } from "./appBase";
import { hasAnyPermission, isSuperAdmin } from "./credoraRoles";
import { isRouteHiddenForRole, resolveRoleHomePath } from "./roleHomeRoutes";
import { ACCOUNTING_PLACEHOLDERS } from "../Components/Credora/Accounting/accountingPlaceholders";
import { financeReportPathKeys } from "../Route/financeReportsRoutes";

export { ROLE_HOME_ROUTE_KEYS, ROLE_HIDDEN_MENU_ROUTE_KEYS } from "./roleHomeRoutes";
import {
  getRemotePathPermissions,
  resolveMenuLinkGrant,
  roleUsesMenuAssignmentMode,
  userUsesMenuAssignmentMode,
} from "./rbacAccess";

function activePathPermissions() {
  return getRemotePathPermissions() || CREDORA_PATH_PERMISSIONS;
}

/**
 * @type {Record<string, string[]>} keys: path after /credora/ (no layout slug)
 *
 * Semantics:
 *   - `[]`            → any authenticated user (with a role) may see it
 *   - `["perm", ...]` → user must hold at least ONE of the listed permissions
 *   - missing key     → DENY (super_admin still bypasses via isSuperAdmin)
 *
 * Keep this map tight: only add entries for paths that should actually be
 * exposed to non-super-admin roles. Unlisted paths are hidden by default.
 */
export const CREDORA_PATH_PERMISSIONS = {
  // Dashboards — gated per audience
  "dashboard/overview": ["reports.read"],
  "dashboard/key-metrics": ["reports.read"],
  "dashboard/today-activity": ["reports.read"],
  "dashboard/executive": ["reports.read"],
  "dashboard/loan-officer": ["loans.read"],
  "dashboard/collections": ["collections.manage"],
  "dashboard/compliance": ["compliance.read"],
  "dashboard/communications": ["communications.read"],
  "dashboard/call-center": ["call_center.read"],

  // AI Insights — only roles with ai.read
  "ai/credit-scoring": ["ai.read"],
  "ai/risk-alerts": ["ai.read"],
  "ai/churn-prediction": ["ai.read"],
  "ai/recommendations": ["ai.read"],

  // Loan Applications
  loans: ["loans.read"],
  "loans/view": ["loans.read"],
  "loans/new": ["loans.create"],
  "loans/new/group": ["loans.create"],
  "loans/groups": ["loans.read"],
  "loans/group-registry": ["loans.read"],
  "loans/drafts": ["loans.read"],
  "loans/pending": ["loans.read"],
  "loans/approved": ["loans.read"],
  "loans/rejected": ["loans.read"],

  // Active loans / EMI / overdue
  "active-loan": ["loans.read"],
  "schedule-emi": ["loans.read"],
  "overdue-loans": ["loans.read", "collections.manage"],
  "restructured-loans": ["loans.read"],

  // Repayments
  "repayments/calendar": ["repayments.read"],
  "repayments/upcoming": ["repayments.read"],
  "repayments/collected": ["repayments.read", "repayments.create"],
  "repayments/overdue": ["repayments.read", "collections.manage"],

  // Risk & Collections — collections roles only
  collections: ["collections.manage"],
  "collections/view": ["collections.manage"],
  "collections/alerts": ["collections.manage"],
  "collections/ptp": ["collections.manage"],
  "collections/write-offs": ["collections.manage"],

  // Compliance & KYC
  "compliance/kyc-verification": ["kyc.verify"],
  "compliance/kyc/pending": ["kyc.verify"],
  "compliance/kyc/verified": ["kyc.verify", "compliance.read"],
  "compliance/kyc/rejected": ["kyc.verify"],
  "compliance/control-registry": ["compliance.read"],
  "compliance/audit-logs": ["audit.read"],
  "compliance/operations": ["compliance.manage"],

  // Borrowers
  borrowers: ["borrowers.read"],
  "borrowers/view": ["borrowers.read"],
  "borrowers/guarantors": ["borrowers.read"],
  "borrowers/collateral": ["borrowers.manage"],
  "borrowers/credit-history": ["crb.read"],

  // Finance — disbursement / fee admin
  "finance/payouts": ["loans.disburse"],
  "finance/disbursements": ["loans.disburse"],
  "finance/fees": ["loans.manage"],
  "finance/working-capital": ["treasury.view"],
  "finance/funding-accounts": ["treasury.manage"],
  "finance/product-allocations": ["treasury.manage"],
  "finance/capital-ledger": ["treasury.view"],

  // Accounting (placeholders)
  ...Object.fromEntries(
    ACCOUNTING_PLACEHOLDERS.map((page) => [
      page.path,
      page.manage ? ["accounting.manage"] : ["accounting.view"],
    ])
  ),

  // Reports
  "reports/portfolio": ["reports.read"],
  "reports/emi-breakdown": ["reports.read"],
  "reports/aging": ["reports.read"],
  "reports/revenue": ["reports.read"],
  "reports/working-capital": ["reports.read", "treasury.view"],
  "reports/funding-accounts": ["reports.read", "treasury.view"],
  "reports/digital-lending": ["reports.read", "compliance.read"],
  "reports/export": ["reports.read", "accounting.view"],
  ...Object.fromEntries(
    financeReportPathKeys().map((key) => [key, ["reports.read", "accounting.view"]])
  ),

  // Integrations — admin only
  "integrations/sms-gateways": ["communications.manage"],
  "integrations/smtp-gateways": ["communications.manage"],
  "integrations/whatsapp-webhooks": ["communications.manage"],
  "integrations/scoring": ["crb.manage"],

  // User Management — list visible to any role with users.read (e.g. auditor);
  // role/team configuration requires users.manage.
  users: ["users.read"],
  "users/roles": ["users.read"],
  "users/teams": ["users.manage"],

  // My account — any signed-in staff user
  account: [],

  // Settings — admins (per-permission gating below)
  settings: ["users.manage"],
  "settings/fee-configuration": ["loans.manage"],
  "settings/loan-products": ["loans.manage"],
  "settings/branches": ["users.manage"],
  "settings/roles": ["users.read"],
  "settings/departments": ["users.manage"],
  "settings/communications": ["communications.manage"],
  "settings/disbursement": ["loans.disburse"],
  "settings/collections": ["collections.manage"],
  "settings/risk-alerts": ["collections.manage"],
  "settings/kyc": ["compliance.manage"],
  "settings/guarantors": ["compliance.manage"],
  "settings/ai": ["ai.run"],
  "settings/security": ["compliance.manage"],
  "settings/treasury": ["treasury.manage"],

  // Communications hub
  communication: ["communications.read"],

  // Call center workspace
  "call-center": ["call_center.read"],

  // Support — open to any authenticated user
  "support/help": [],
  "support/docs": [],
  "support/contact": [],
};

const pub = process.env.PUBLIC_URL || "";

function stripDeployPrefix(path) {
  let p = stripAppBasename(path || "");
  if (pub && p.startsWith(pub)) {
    p = p.slice(pub.length) || "/";
  }
  return p;
}

/** True when a path resolves into the /credora/* tree (after stripping PUBLIC_URL). */
function isCredoraPath(path) {
  if (!path) {
    return false;
  }
  let p = stripDeployPrefix(path).replace(/^\//, "");
  return p.startsWith("credora/");
}

/** Strip PUBLIC_URL and /:layout suffix; return credora route key e.g. "loans/pending". */
export function credoraRouteKey(path) {
  if (!path) {
    return "";
  }
  let p = stripDeployPrefix(path).replace(/^\//, "");
  if (!p.startsWith("credora/")) {
    return "";
  }
  p = p.slice("credora/".length);
  const parts = p.split("/").filter(Boolean);
  if (parts.length && /^[A-Z][a-z]+$/.test(parts[parts.length - 1])) {
    parts.pop();
  }
  let key = parts.join("/");
  const map = activePathPermissions();
  while (key) {
    if (Object.prototype.hasOwnProperty.call(map, key)) {
      return key;
    }
    const idx = key.lastIndexOf("/");
    key = idx >= 0 ? key.slice(0, idx) : "";
  }
  return parts[0] || "";
}

/**
 * Returns the permission list for a credora path, or `null` when:
 *   - the path is not a credora path, OR
 *   - the path is credora but not explicitly mapped (DENY by default).
 *
 * Callers must distinguish these two `null` cases via `canAccessMenuItem`.
 */
export function permissionsForCredoraPath(path) {
  const key = credoraRouteKey(path);
  if (!key) {
    return null;
  }
  const map = activePathPermissions();
  if (Object.prototype.hasOwnProperty.call(map, key)) {
    return map[key];
  }
  return null;
}

/**
 * MNH-style: user needs at least one permission in the list.
 * - empty list (`[]`)       → any authenticated user (with a role)
 * - null / unmapped credora → DENIED (must be added to CREDORA_PATH_PERMISSIONS)
 * - null / non-credora      → allowed (path is outside the gated area)
 */
export function canAccessMenuItem(user, item) {
  if (isSuperAdmin(user)) {
    return true;
  }
  if (!item?.path) {
    return true;
  }
  const pathKey = credoraRouteKey(item.path);
  if (pathKey && isRouteHiddenForRole(user, pathKey)) {
    return false;
  }
  const assignmentMode =
    userUsesMenuAssignmentMode(user) || roleUsesMenuAssignmentMode(user?.role);

  if (pathKey) {
    const grantDecision = resolveMenuLinkGrant(user, pathKey);
    if (grantDecision === true) {
      return true;
    }
    if (grantDecision === false) {
      return false;
    }
    if (assignmentMode) {
      return false;
    }
  }

  const perms = item.permissions ?? permissionsForCredoraPath(item.path);
  if (perms == null) {
    return !isCredoraPath(item.path);
  }
  if (!perms.length) {
    return Boolean(user?.role);
  }
  if (!user?.role) {
    return false;
  }
  return hasAnyPermission(user, perms);
}

export function canAccessCredoraPath(user, path) {
  return canAccessMenuItem(user, { path, permissions: permissionsForCredoraPath(path) });
}

/** Attach permissions[] to every menu link (like MNH menu JSON). */
export function attachMenuPermissions(item) {
  if (!item) {
    return item;
  }
  const next = { ...item };
  if (next.path) {
    // Preserve `null` (= deny by default for unmapped credora paths).
    // Do NOT coerce to [] — that would mean "any authenticated user".
    next.permissions = permissionsForCredoraPath(next.path);
  }
  if (next.children?.length) {
    next.children = next.children.map(attachMenuPermissions);
  }
  return next;
}

export function enrichMenuWithPermissions(menu = MENUITEMS) {
  return menu.map((group) => ({
    ...group,
    Items: (group.Items || []).map(attachMenuPermissions),
  }));
}

/** Filter children recursively; drop nested groups with no visible links. */
export function filterVisibleMenuChildren(user, children = []) {
  if (!children?.length) {
    return [];
  }
  if (isSuperAdmin(user)) {
    return children;
  }
  return children
    .map((child) => {
      if (child.children?.length) {
        const nested = filterVisibleMenuChildren(user, child.children);
        if (!nested.length) {
          return null;
        }
        return { ...child, children: nested };
      }
      return canAccessMenuItem(user, child) ? child : null;
    })
    .filter(Boolean);
}

export function hasAnyVisibleMenuSection(user, section) {
  if (isSuperAdmin(user)) {
    return true;
  }
  if (section.type === "link") {
    return canAccessMenuItem(user, section);
  }
  return filterVisibleMenuChildren(user, section.children).length > 0;
}

function cloneMenuSection(section) {
  return {
    ...section,
    children: section.children ? section.children.map((c) => ({ ...c })) : undefined,
  };
}

/** Pre-filter menu for bookmarks / home path (also enforced at render in SidebarMenuItems). */
export function filterMenuByUser(user, menu = enrichMenuWithPermissions()) {
  if (isSuperAdmin(user)) {
    return menu;
  }
  if (!user?.role) {
    return menu.map((group) => ({ ...group, Items: [] }));
  }

  return menu.map((group) => {
    const items = (group.Items || [])
      .map((section) => {
        if (!hasAnyVisibleMenuSection(user, section)) {
          return null;
        }
        if (section.type !== "sub" || !section.children) {
          return canAccessMenuItem(user, section) ? cloneMenuSection(section) : null;
        }
        const children = filterVisibleMenuChildren(user, section.children);
        if (!children.length) {
          return null;
        }
        return { ...cloneMenuSection(section), children };
      })
      .filter(Boolean);
    return { ...group, Items: items };
  });
}

export function getMenuForUser(user) {
  return filterMenuByUser(user);
}

/** Role-specific home path with permission check; falls back to first accessible menu link. */
export function roleHomePath(user, layoutSlug = DEFAULT_LAYOUT_SLUG) {
  const roleHome = resolveRoleHomePath({
    user,
    layoutSlug,
    pub,
    canAccess: canAccessCredoraPath,
  });
  if (roleHome) {
    return roleHome;
  }
  return defaultCredoraHomePath(user, layoutSlug);
}

/** First navigable credora path for post-login / forbidden redirect. */
export function defaultCredoraHomePath(user, layoutSlug = DEFAULT_LAYOUT_SLUG) {
  const menu = getMenuForUser(user);
  for (const group of menu) {
    for (const section of group.Items || []) {
      const visible = filterVisibleMenuChildren(user, section.children);
      const link = section.path || visible[0]?.path;
      if (link && canAccessMenuItem(user, { path: link })) {
        return `${link}/${layoutSlug}`;
      }
      if (visible[0]?.path) {
        return `${visible[0].path}/${layoutSlug}`;
      }
    }
  }
  return `${pub}/credora/support/help/${layoutSlug}`;
}
